/**
 * Quantization helpers.
 *
 * Every float that goes on the wire is mapped to an unsigned integer of a fixed
 * width. Two rules follow from that, and both matter:
 *
 * 1. There are no signed integers in the protocol. A range like pitch
 *    [-π/2, π/2] is mapped onto [0, 2^bits - 1], so the codec never deals with
 *    two's complement.
 * 2. Delta comparisons must be made on *quantized* values, not raw floats.
 *    Comparing floats would mark a field as changed when its quantized form is
 *    identical, sending bytes that carry no information.
 */

export const TAU = Math.PI * 2;

export function maxValueForBits(bits: number): number {
  return 2 ** bits - 1;
}

/** Largest error quantization can introduce over a range at a given width. */
export function quantizationError(min: number, max: number, bits: number): number {
  return (max - min) / maxValueForBits(bits) / 2;
}

/** Maps a float in [min, max] onto an unsigned integer of `bits` width. Clamps. */
export function quantize(value: number, min: number, max: number, bits: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  const scale = maxValueForBits(bits) / (max - min);
  return Math.round((clamped - min) * scale);
}

/** Inverse of `quantize`. */
export function dequantize(quantized: number, min: number, max: number, bits: number): number {
  const scale = (max - min) / maxValueForBits(bits);
  return min + quantized * scale;
}

/** Normalizes any angle into [0, TAU). */
export function wrapAngle(radians: number): number {
  const wrapped = radians % TAU;
  return wrapped < 0 ? wrapped + TAU : wrapped;
}

/**
 * Quantizes a heading. Wraps first so callers never have to, and maps onto
 * [0, 2^bits - 1] across a *full* turn — note the divisor is 2^bits, not
 * 2^bits - 1, because 0 and TAU are the same heading and must not both be
 * representable.
 */
export function quantizeAngle(radians: number, bits: number): number {
  const steps = 2 ** bits;
  const normalized = wrapAngle(radians) / TAU;
  return Math.round(normalized * steps) % steps;
}

export function dequantizeAngle(quantized: number, bits: number): number {
  const steps = 2 ** bits;
  return (quantized / steps) * TAU;
}

/** Largest error `quantizeAngle` can introduce, in radians. */
export function angleQuantizationError(bits: number): number {
  return TAU / 2 ** bits / 2;
}
